/**
 * ---------------------------------------------------------------------------
 * Add to cart — flight animation
 * ---------------------------------------------------------------------------
 * When a product is added to the cart, a clone of its image hops up, arcs
 * across the screen and drops into the cart, which then bumps.
 *
 * Two triggers, both delegated so they survive the drawer's innerHTML swaps:
 *
 *   1. [data-action="add-to-cart"]   — product page / quick view forms
 *   2. [data-cart-drawer-quick-add]  — the upsell strip inside the drawer
 *
 * Where it flies to depends on what the shopper can actually see: with the
 * drawer open the header cart icon sits *behind* the panel, so the item flies
 * to the drawer's own item count instead. Otherwise it flies to the header
 * cart icon.
 *
 * Deliberately decorative and fully self-contained: it never touches the cart
 * request, never blocks it, and any failure to find an image or a target just
 * means no animation. Nothing here can stop a product being added.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var DURATION = 780;
  var CLONE_CLASS = 'cart-fly';
  var BUMP_CLASS = 'cart-fly-target-bump';
  var BUMP_MS = 460;
  // Cap the clone so a large gallery image does not fly across as a full-size
  // panel; the aspect ratio of the source is preserved within this box.
  var MAX_SIZE = 140;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return !!(reduceMotion && reduceMotion.matches);
  }

  // Web Animations is the only hard requirement; without it we simply no-op.
  var supported = typeof document.createElement('div').animate === 'function';

  /* -------------------------------------------------------------------------
   * Finding the image to fly
   * ---------------------------------------------------------------------- */

  /**
   * Lazyloaded gallery images may still be showing a placeholder in `src`, so
   * prefer the resolved source and fall back to the lazyload attributes.
   */
  function sourceFor(img) {
    if (!img) {
      return '';
    }

    var candidate = img.currentSrc || img.getAttribute('src') || '';

    // lazysizes placeholders are 1x1 gifs / data URIs — not worth flying.
    if (!candidate || candidate.indexOf('data:') === 0) {
      candidate = img.getAttribute('data-src') || img.getAttribute('data-zoom') || candidate;
    }

    return candidate;
  }

  function firstVisible(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var rect = nodes[i].getBoundingClientRect();

      if (rect.width > 8 && rect.height > 8) {
        return nodes[i];
      }
    }

    return null;
  }

  function imageForQuickAdd(button) {
    var card = button.closest('.cart-drawer__rec');

    return card ? card.querySelector('img.cart-drawer__rec-image') : null;
  }

  function imageForAddToCart(button) {
    // Scope the search to the product's own section so a button in a quick view
    // or a featured-product block picks up that product's image, not the page's.
    var scope =
      button.closest('.product-quick-view') ||
      button.closest('[data-section-type="product"]') ||
      button.closest('.shopify-section') ||
      document;

    // The gallery marks the visible slide; prefer it over the first image so a
    // shopper who has switched variant or slide sees the image they are looking at.
    var selected = scope.querySelector(
      '.product-gallery__carousel-item.is-selected img.product-gallery__image'
    );

    if (selected) {
      return selected;
    }

    return firstVisible(
      scope.querySelectorAll('img.product-gallery__image, .product-gallery img, .product-item__image')
    );
  }

  /* -------------------------------------------------------------------------
   * Finding where to fly to
   * ---------------------------------------------------------------------- */

  function isDrawerOpen() {
    var drawer = document.getElementById('mini-cart');

    return !!(
      drawer &&
      drawer.classList.contains('cart-drawer') &&
      drawer.getAttribute('aria-hidden') === 'false'
    );
  }

  function target() {
    // With the drawer open the header icon is covered by the panel, so landing
    // there would look like the item vanishing behind it.
    if (isDrawerOpen()) {
      var count = document.querySelector('.cart-drawer__count') ||
        document.querySelector('.cart-drawer__title');

      if (count) {
        return count;
      }
    }

    return (
      document.querySelector('.header__action-item--cart') ||
      document.querySelector('[data-action="toggle-mini-cart"]')
    );
  }

  /* -------------------------------------------------------------------------
   * The flight
   * ---------------------------------------------------------------------- */

  function bump(node) {
    if (!node) {
      return;
    }

    node.classList.remove(BUMP_CLASS);
    // Force a reflow so the class re-triggers when items are added in quick
    // succession, rather than the animation being ignored as already running.
    void node.offsetWidth;
    node.classList.add(BUMP_CLASS);

    setTimeout(function () {
      node.classList.remove(BUMP_CLASS);
    }, BUMP_MS);
  }

  function fly(img) {
    var destination = target();

    if (!img || !destination) {
      return;
    }

    var from = img.getBoundingClientRect();
    var to = destination.getBoundingClientRect();

    if (!from.width || !from.height || !to.width) {
      return;
    }

    var src = sourceFor(img);

    if (!src) {
      return;
    }

    // Preserve the source aspect ratio inside the size cap.
    var scale = Math.min(1, MAX_SIZE / Math.max(from.width, from.height));
    var width = Math.round(from.width * scale);
    var height = Math.round(from.height * scale);

    var clone = document.createElement('div');
    clone.className = CLONE_CLASS;
    clone.setAttribute('aria-hidden', 'true');
    clone.style.width = width + 'px';
    clone.style.height = height + 'px';

    var picture = document.createElement('img');
    picture.className = CLONE_CLASS + '__image';
    picture.src = src;
    picture.alt = '';
    clone.appendChild(picture);

    document.body.appendChild(clone);

    // Travel measured centre to centre, from the clone's own top-left origin.
    var startX = from.left + from.width / 2 - width / 2;
    var startY = from.top + from.height / 2 - height / 2;
    var dx = to.left + to.width / 2 - (startX + width / 2);
    var dy = to.top + to.height / 2 - (startY + height / 2);

    // Lift enough to read as a hop even when the cart is more or less level
    // with the button, but never so far that it leaves the viewport.
    var lift = Math.max(90, Math.abs(dy) * 0.45);
    lift = Math.min(lift, startY + 40);

    var base = 'translate3d(' + startX + 'px, ' + startY + 'px, 0)';

    var animation = clone.animate(
      [
        { transform: base + ' translate3d(0, 0, 0) scale(1)', opacity: 1, offset: 0 },
        // The jump: up and slightly bigger before it sets off.
        {
          transform: base + ' translate3d(' + dx * 0.12 + 'px, ' + (-lift * 0.85) + 'px, 0) scale(1.08)',
          opacity: 1,
          offset: 0.24
        },
        // Apex of the arc.
        {
          transform: base + ' translate3d(' + dx * 0.55 + 'px, ' + (dy * 0.3 - lift) + 'px, 0) scale(0.72)',
          opacity: 1,
          offset: 0.58
        },
        // Drop into the cart.
        {
          transform: base + ' translate3d(' + dx + 'px, ' + dy + 'px, 0) scale(0.14)',
          opacity: 0.25,
          offset: 1
        }
      ],
      {
        duration: DURATION,
        easing: 'cubic-bezier(0.42, 0, 0.4, 1)',
        fill: 'forwards'
      }
    );

    var landed = false;

    function finish() {
      if (landed) {
        return;
      }

      landed = true;

      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }

      // Re-resolve the target: the drawer may have re-rendered mid-flight, which
      // replaces the element we measured against.
      bump(target());
    }

    animation.onfinish = finish;
    animation.oncancel = finish;
    // Safety net in case the tab is backgrounded and onfinish never arrives.
    setTimeout(finish, DURATION + 400);
  }

  /* -------------------------------------------------------------------------
   * Triggers
   * ---------------------------------------------------------------------- */

  if (!supported) {
    return;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (prefersReducedMotion() || !event.target.closest) {
        return;
      }

      var quickAdd = event.target.closest('[data-cart-drawer-quick-add]');

      if (quickAdd) {
        if (!quickAdd.disabled) {
          fly(imageForQuickAdd(quickAdd));
        }

        return;
      }

      var addButton = event.target.closest('[data-action="add-to-cart"]');

      if (addButton && !addButton.disabled) {
        fly(imageForAddToCart(addButton));
      }
    },
    // Capture, so the image is measured before theme.js starts mutating the
    // button and, on the upsell strip, before the drawer re-renders.
    true
  );
})();
